import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";
import PurchasePaymentTable from "./components/PurchasePaymentTable";
import AddPurchasePaymentModal from "./components/AddPurchasePaymentModal";
import { useGetPurchasePayments } from "./useGetPurchasePayments";
import { useCreatePurchasePayment } from "./useCreatePurchasePayment";
import { useVoidPurchasePayment } from "./useVoidPurchasePayment";

const PAYMENT_MODES = [
  "Bank Deposit",
  "Cash",
  "Cheque",
  "Credit Card",
  "Debit Card",
  "Demand Draft",
  "Online Payment Gateway",
  "Online Transfer",
  "PayPal",
  "Wire Transfer",
];

const PurchasePaymentsContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const location = useLocation();
  const prefill = location.state?.prefillExpense || null;

  const {
    data: paymentsData = [],
    isLoading,
    isError,
  } = useGetPurchasePayments();

  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    if (paymentsData.length) setPayments(paymentsData);
  }, [paymentsData]);

  // Auto-open modal with prefill when redirected from Expense "Record Payment"
  useEffect(() => {
    if (prefill) {
      setEditingPayment(null);
      setIsModalOpen(true);
    }
  }, [prefill]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, modeFilter]);

  const filtered = payments.filter((p) => {
    const matchSearch =
      !searchTerm.trim() ||
      p.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.expenseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchMode = modeFilter === "all" || p.paymentMode === modeFilter;
    return matchSearch && matchMode;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const createPayment = useCreatePurchasePayment();
  const voidPayment = useVoidPurchasePayment();

  const handleAdd = async (data) => {
    try {
      await createPayment.mutateAsync({
        vendorId: data.vendorId,
        paymentDate: data.date,
        paymentMode: data.paymentMode,
        amount: data.amount,
        depositTo: data.paidFrom || null,
        reference: data.reference || null,
        comments: data.comments || null,
        allocations: data.allocations || [],
      });
      toast.success("Payment recorded");
      setIsModalOpen(false);
    } catch (err) {
      toast.error(err?.message || "Failed to record payment");
    }
  };

  // The backend has no update-payment endpoint; a recorded payment is voided
  // and re-created rather than edited.
  const handleEdit = () => {
    toast("To change a recorded payment, void it and create a new one.", {
      icon: "ℹ️",
    });
    setEditingPayment(null);
    setIsModalOpen(false);
  };

  const handleDelete = (id) =>
    voidPayment.mutate(
      { id, reason: "Voided from payments list" },
      {
        onSuccess: () => toast.success("Payment voided"),
        onError: (err) => toast.error(err?.message || "Failed to void payment"),
      },
    );

  // Stats
  const totalPaid = payments.reduce(
    (s, p) => s + (parseFloat(p.amount) || 0),
    0,
  );
  const modeCounts = PAYMENT_MODES.reduce((acc, m) => {
    acc[m] = payments.filter((p) => p.paymentMode === m).length;
    return acc;
  }, {});
  const topMode =
    Object.entries(modeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <main className="p-4 md:p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                {t("purchasePayments.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("purchasePayments.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                {t("actions.export")}
              </Button>
              <Button
                variant="default"
                size="md"
                iconName="Plus"
                onClick={() => {
                  setEditingPayment(null);
                  setIsModalOpen(true);
                }}
              >
                {t("actions.addPayment")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Prefill notice */}
        <AnimatePresence>
          {prefill && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 flex gap-3 p-4 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/10 dark:border-blue-800"
            >
              <Icon
                name="Info"
                size={18}
                className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
              />
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Recording payment for expense{" "}
                <strong>{prefill.expenseNumber}</strong> — {prefill.vendorName}.
                Amount due:{" "}
                <strong>
                  SR{" "}
                  {parseFloat(prefill.amountDue || 0).toLocaleString("en-SA", {
                    minimumFractionDigits: 2,
                  })}
                </strong>
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {[
            {
              label: t("payments.stats.totalPayments"),
              value: payments.length,
              icon: "Receipt",
              color:
                "text-blue-500 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
            },
            {
              label: t("purchasePayments.totalPaid"),
              value: `SR ${totalPaid.toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
              icon: "TrendingDown",
              color:
                "text-red-500 bg-red-100 dark:bg-red-900/20 dark:text-red-400",
            },
            {
              label: t("payments.stats.thisMonth"),
              value: payments.filter((p) => {
                const d = new Date(p.date);
                const n = new Date();
                return (
                  d.getMonth() === n.getMonth() &&
                  d.getFullYear() === n.getFullYear()
                );
              }).length,
              icon: "Calendar",
              color: "text-primary bg-primary/10",
            },
            {
              label: t("payments.stats.topMode"),
              value: t(`paymentModes.${topMode}`, { defaultValue: topMode }),
              icon: "Wallet",
              color:
                "text-orange-500 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-card rounded-xl p-4 shadow-sm border border-border flex items-center gap-3"
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.color}`}
              >
                <Icon name={stat.icon} size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold text-foreground truncate">
                  {stat.value}
                </p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Mode filter tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="mb-6"
        >
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setModeFilter("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                modeFilter === "all"
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "bg-card text-muted-foreground hover:bg-muted border border-border"
              }`}
            >
              {t("common.all")}
              <span
                className={`ms-2 px-2 py-0.5 rounded-full text-xs ${modeFilter === "all" ? "bg-white/20" : "bg-muted text-muted-foreground"}`}
              >
                {payments.length}
              </span>
            </button>
            {PAYMENT_MODES.filter((m) => modeCounts[m] > 0).map((mode) => (
              <button
                key={mode}
                onClick={() => setModeFilter(mode)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  modeFilter === mode
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-card text-muted-foreground hover:bg-muted border border-border"
                }`}
              >
                {t(`paymentModes.${mode}`, { defaultValue: mode })}
                <span
                  className={`ms-2 px-2 py-0.5 rounded-full text-xs ${modeFilter === mode ? "bg-white/20" : "bg-muted text-muted-foreground"}`}
                >
                  {modeCounts[mode]}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-card rounded-xl p-5 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("purchasePayments.searchPlaceholder")}
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={15} />
              </button>
            )}
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <PurchasePaymentTable
            payments={paginated}
            onEdit={(p) => {
              setEditingPayment(p);
              setIsModalOpen(true);
            }}
            onDelete={handleDelete}
          />
        </motion.div>

        {/* Empty state */}
        {filtered.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-4"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Wallet" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("purchasePayments.emptyTitle")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || modeFilter !== "all"
                ? t("common.adjustFiltersOrSearch")
                : t("purchasePayments.emptyDescription")}
            </p>
            {!searchTerm && modeFilter === "all" && (
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => {
                  setEditingPayment(null);
                  setIsModalOpen(true);
                }}
              >
                {t("actions.addPayment")}
              </Button>
            )}
          </motion.div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t("common.showingRange", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, filtered.length),
                total: filtered.length,
                entity: t("entities.payments"),
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />
              <span className="text-sm text-foreground px-2">
                {t("common.pageOf", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {isModalOpen && (
          <AddPurchasePaymentModal
            existingPayment={editingPayment}
            prefillExpense={prefill}
            nextPaymentNumber={`PP-${String(payments.length + 1).padStart(4, "0")}`}
            onSave={editingPayment ? handleEdit : handleAdd}
            onClose={() => {
              setIsModalOpen(false);
              setEditingPayment(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const PurchasePayments = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <PurchasePaymentsContent />
    </div>
  </SidebarProvider>
);

const LoadingSkeleton = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
  >
    <main className="p-4 md:p-6 animate-pulse">
      <div className="h-8 bg-muted rounded w-64 mb-2" />
      <div className="h-4 bg-muted rounded w-96 mb-8" />
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl" />
        ))}
      </div>
      <div className="flex gap-2 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-9 bg-muted rounded-lg w-28" />
        ))}
      </div>
      <div className="h-14 bg-muted rounded-xl mb-6" />
      <div className="bg-card rounded-xl border border-border p-6 space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-14 bg-muted rounded" />
        ))}
      </div>
    </main>
  </div>
);

export default PurchasePayments;
